import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { getUserHistory } from '../../api/historyApi';
import { submitRecommendation } from '../../api/recommendationAPI';
import '../Profile.css';

const formatDate = (iso) => {
  if (!iso) return 'תאריך לא ידוע';
  const d = new Date(iso);
  return d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const STATUS_LABELS = {
  pending: 'ההמלצה מחכה לאישור',
  approved: 'ההמלצה אושרה',
};

const RecommendationPopup = ({ item, orderId, onClose, onSent }) => {
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!name.trim() || !text.trim()) { setError('יש למלא את כל השדות'); return; }
    setLoading(true);
    setError(null);
    try {
      await submitRecommendation({ orderId, itemId: item.item_id, name, text });
      onSent();
    } catch (e) {
      setError('שגיאה בשליחה, נסי שוב');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rec-popup-overlay" onClick={onClose}>
      <div className="rec-popup" onClick={e => e.stopPropagation()}>
        <button className="rec-popup-close" onClick={onClose}>✕</button>
        <h3 className="rec-popup-title">המלצה על מוצר</h3>
        <p className="rec-popup-product">{item.name}</p>
        <input
          className="rec-popup-input"
          placeholder="שם הממליצה"
          value={name}
          onChange={e => setName(e.target.value)}
          dir="rtl"
        />
        <textarea
          className="rec-popup-textarea"
          placeholder="ההמלצה שלך"
          value={text}
          onChange={e => setText(e.target.value)}
          rows={4}
          dir="rtl"
        />
        {error && <div className="rec-popup-error">{error}</div>}
        <button className="rec-popup-submit" onClick={handleSubmit} disabled={loading}>
          {loading ? 'שולח...' : 'שליחת המלצה לאישור'}
        </button>
      </div>
    </div>
  );
};

const getPendingCount = (items, sentItems, orderId) => {
  return (items || []).filter(item => {
    const key = `${orderId}_${item.item_id}`;
    const status = sentItems[key] ? 'pending' : item.recommendation_status;
    return !status || status === 'none' || status === 'אין המלצה' || status === 'not_recommended';
  }).length;
};

const History = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [popupItem, setPopupItem] = useState(null);
  const [sentItems, setSentItems] = useState({});

  useEffect(() => {
    if (!user) { navigate('/', { replace: true }); return; }
    setLoading(true);
    setError(null);
    getUserHistory(user.id)
      .then(data => setHistory(data || []))
      .catch(e => setError(e.message || 'שגיאה בטעינת היסטוריה'))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  if (!user) return null;

  const handleSent = (orderId, itemId) => {
    setSentItems(prev => ({ ...prev, [`${orderId}_${itemId}`]: true }));
    setPopupItem(null);
  };

  return (
    <div className="history-page">
      {loading && <div className="history-status">טוען היסטוריה...</div>}
      {error && <div className="history-status" style={{ color: 'red' }}>{error}</div>}
      {!loading && !error && history.length === 0 && (
        <div className="history-status">אין היסטוריית רכישות.</div>
      )}

      <div className="history-container">
        {history.map((purchase) => {
          const pendingCount = getPendingCount(purchase.items, sentItems, purchase.id);
          return (
            <div key={purchase.id} className="history-purchase-card">
              <div
                className={`history-purchase-header ${openId === purchase.id ? 'open' : ''}`}
                onClick={() => setOpenId(openId === purchase.id ? null : purchase.id)}
              >
                <span className="history-order-id">הזמנה #{purchase.id}</span>
                <span className="history-header-dot"></span>
                <span className="history-date">{formatDate(purchase.created_at)}</span>
                <span className="history-header-dot"></span>
                <span className="history-items-count">{purchase.items?.length || 0} פריטים</span>
                <span className="history-header-rec-slot">
                  {pendingCount > 0 && (
                    <span className="history-pending-badge">כנסי להמליץ</span>
                  )}
                </span>
                <span className="history-total">₪{Number(purchase.total_price).toFixed(2)}</span>
                <span className="history-expand-icon">{openId === purchase.id ? '▲' : '▼'}</span>
              </div>

              {openId === purchase.id && (
                <div className="history-products-list">
                  {(purchase.items || []).map((item, idx) => {
                    const key = `${purchase.id}_${item.item_id}`;
                    const status = sentItems[key] ? 'pending' : item.recommendation_status;
                    const noRec = !status || status === 'none' || status === 'אין המלצה' || status === 'not_recommended';
                    return (
                      <div key={item.item_id || idx} className="history-product-item">
                        <div className="history-product-name">{item.name}</div>
                        <div className="history-product-rec">
                          {noRec ? (
                            <button
                              className="rec-now-btn"
                              onClick={e => { e.stopPropagation(); setPopupItem({ item, orderId: purchase.id }); }}
                            >
                              המליצי כאן
                            </button>
                          ) : (
                            <span className={`rec-status-badge rec-status-${status}`}>
                              {STATUS_LABELS[status] || status}
                            </span>
                          )}
                        </div>
                        <div className="history-product-meta">
                          {item.quantity > 1 && (
                            <span className="history-product-quantity">כמות: {item.quantity}</span>
                          )}
                          {item.list_price && Number(item.list_price) !== Number(item.purchase_price) && (
                            <span className="history-product-original">₪{Number(item.list_price).toFixed(2)}</span>
                          )}
                          <span className="history-product-price">₪{Number(item.purchase_price).toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })}
                  {Number(purchase.coupon_discount) !== 0 && (
                    <div className="history-coupon-discount">
                      <span>הנחה בקנייה:</span>
                      <span>₪{Math.abs(Number(purchase.coupon_discount)).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {popupItem && (
        <RecommendationPopup
          item={popupItem.item}
          orderId={popupItem.orderId}
          onClose={() => setPopupItem(null)}
          onSent={() => handleSent(popupItem.orderId, popupItem.item.item_id)}
        />
      )}
    </div>
  );
};

export default History;
